import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { useCallback } from 'react';

import { useDisabledDatePresets } from '@/App/tiles/shared/useDisabledDatePresets.tsx';
import {
  useCheckTargetAccumulatorByTradeCapture,
  useTargetAccumulatorSelector,
  useTargetAccumulatorTileId,
} from '@/App/tiles/TargetAccumulator/TargetAccumulatorContext.tsx';
import { TargetAccumulatorFormField } from '@/App/tiles/TargetAccumulator/TargetAccumulatorFormField.tsx';
import { DatePickerWithTenorNext } from '@/components/ui/DatePickerWithTenorNext.tsx';
import { TradeCaptureValidationWrapper } from '@/components/ui/TradeCaptureFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import {
  selectTargetAccumulatorLastFixing,
  selectTargetAccumulatorValidation,
} from '@/store/targetAccumulator/selectors.ts';

const LastFixingDate = () => {
  const tileId = useTargetAccumulatorTileId();
  const { lastFixingDate, lastFixingTenor } = useAppSelector((state) =>
    selectTargetAccumulatorLastFixing(state, tileId),
  );
  const currencyPair = useTargetAccumulatorSelector((targetAccumulator) => targetAccumulator?.properties.currencyPair);
  const disabledDatePresets = useDisabledDatePresets(tileId, currencyPair);

  const validation = useAppSelector((state) => selectTargetAccumulatorValidation(state, tileId));

  const { sgwtWebAnalytics } = useSgwtWidgets();

  const checkTargetAccumulatorWithTradeCapture = useCheckTargetAccumulatorByTradeCapture();

  const handleDateChange = useCallback(
    (dateOrTenor: string) => {
      sgwtWebAnalytics?.trackEvent('TargetAccumulator', 'Last Fixing Date changed', dateOrTenor);

      checkTargetAccumulatorWithTradeCapture({
        lastFixingDate: dateOrTenor,
      });
    },
    [checkTargetAccumulatorWithTradeCapture, sgwtWebAnalytics],
  );

  const getValidationState = () => {
    if (validation?.lastFixingDate?.some((v) => v.type === 'error')) {
      return 'invalid';
    }

    if (validation?.lastFixingDate?.some((v) => v.type === 'warning')) {
      return 'warning';
    }

    return undefined;
  };

  return (
    <TargetAccumulatorFormField formattedMessageId="targetAccumulator.form.lastFixingDate">
      <TradeCaptureValidationWrapper validation={validation?.lastFixingDate}>
        <DatePickerWithTenorNext
          date={lastFixingDate}
          tenor={lastFixingTenor}
          data-e2e="last-fixing-date-input"
          onChange={handleDateChange}
          validationState={getValidationState()}
          disablePresets={disabledDatePresets}
        />
      </TradeCaptureValidationWrapper>
    </TargetAccumulatorFormField>
  );
};

export default LastFixingDate;
